<template>
  <main>
    <top-bar class="main-top-bar" />
    <section class="scrollable-content">
      <slot />
    </section>
    <footer class="bottom-bar">
      <v-btn
        v-if="user?.id"
        block
        size="x-large"
        color="primary"
        class="typography-button-md"
        prepend-icon="mdi-plus"
        @click="createNewMoveTask">
        New Move
      </v-btn>
    </footer>
  </main>
</template>
<script setup lang="ts">
import TopBar from '@/components/TopBar.vue';
import { useTaskQueries, useDialogTrigger, useNotify } from '@/composables';
import { useAuthStore } from '@/store/useAuthStore.ts';
import { useWarehouseStore } from '@/store/useWarehouseStore.ts';
import { YardTaskType, type MoveTaskViewData } from '@nova/core';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';

const { trackYardTaskCreatedNewMove } = useYardTaskAnalytics();
const { useCreateTaskMutation } = useTaskQueries();
const createMoveTaskMutation = useCreateTaskMutation();
const { user } = useAuthStore();
const { selectedWarehouseId } = useWarehouseStore();
const { notify } = useNotify();
const editTaskDialog = useDialogTrigger('open-edit-task-dialog');

// TODO: API needs to allow creating move task without child details
const createNewMoveTask = async () => {
  if (!selectedWarehouseId || !user?.id) {
    return;
  }
  const payload = {
    warehouseId: selectedWarehouseId,
    type: YardTaskType.MOVE,
    child: {
      assetContainerId: null,
      originSpotId: null,
      destinationSpotId: null
    },
    assignedTo: user.id,
    description: 'Move task created by the driver.'
  };
  try {
    const task = await createMoveTaskMutation.mutateAsync({ params: payload });

    if (task.id) {
      editTaskDialog.triggerDialog({ taskId: task.id });
      try {
        trackYardTaskCreatedNewMove(task as MoveTaskViewData);
      } catch (analyticsError) {
        console.error('Failed to track yard task created new move:', analyticsError);
      }
    } else {
      throw new Error(`Failed to call createMoveTaskMutation: ${JSON.stringify(task)}`);
    }
  } catch (error) {
    console.error('Error creating new move task:', error);
    notify('Failed to create new move task', 'error');
  }
};
</script>

<style scoped lang="scss">
main {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.main-top-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: $color-neutral-0;
  flex-shrink: 0;
}

.scrollable-content {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.bottom-bar {
  position: sticky;
  bottom: 0;
  background: $color-neutral-0;
  padding: $spacing-4;
  z-index: 10;
  border-top: 1px solid $color-line-divider;
  flex-shrink: 0;
}
</style>
